<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-4">Задачи с товарами</h5>

      <div class="row g-4">
        <div class="col-md-6">
          <label class="form-label" for="offerTaskStore">Магазин</label>
          <select
              id="offerTaskStore"
              class="form-select"
              v-model="selectedStoreId"
              :disabled="running"
              @change="onStoreChange">
            <option value="" disabled>Выберите магазин</option>
            <option v-for="store in supportedStores" :key="store.id" :value="store.id">
              {{ store.name }} — {{ marketplaceTitle(store.marketplace) }}
            </option>
          </select>
        </div>

        <div class="col-md-6">
          <label class="form-label" for="offerTaskType">Задача</label>
          <select
              id="offerTaskType"
              class="form-select"
              v-model="selectedTaskType"
              :disabled="running || !availableTaskTypes.length">
            <option value="" disabled>Выберите задачу</option>
            <option v-for="type in availableTaskTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="selectedTaskType === 'ozon_reset_category_errors'" class="form-check mt-3">
        <input
            class="form-check-input"
            type="checkbox"
            id="dryRun"
            v-model="dryRun"
            :disabled="running">
        <label class="form-check-label" for="dryRun">
          Только просмотр (dry run) — показать товары с ошибками категорий без сброса
        </label>
      </div>

      <p class="text-muted mt-3 mb-4">
        Выберите магазин и задачу для запуска фоновой обработки товаров.
      </p>

      <div v-if="!supportedStores.length" class="alert alert-warning" role="alert">
        Нет магазинов Ozon. Сброс ошибок категорий доступен только для Ozon.
      </div>

      <ButtonComponent
          ref="runButton"
          :class="'btn btn-primary'"
          :disabled="!canRun"
          @click="run">
        Запустить задачу
      </ButtonComponent>

      <div v-if="task" class="mt-4">
        <hr class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">{{ task.name || 'Задача с товарами' }}</h6>
          <span class="badge" :class="resultBadgeClass">{{ resultDescription }}</span>
        </div>

        <div class="progress mb-2" style="height: 20px;">
          <div
              class="progress-bar"
              :class="{'progress-bar-striped progress-bar-animated': isInProgress}"
              role="progressbar"
              :style="{width: progress + '%'}"
              :aria-valuenow="progress"
              aria-valuemin="0"
              aria-valuemax="100">
            {{ progress }} %
          </div>
        </div>

        <p v-if="task.task_info" class="mb-0" :class="isFailure ? 'text-danger' : 'text-muted'">
          {{ task.task_info }}
        </p>

        <RouterLink
            v-if="task.id"
            :to="{name: 'task-detail', params: {id: task.id}}"
            class="d-inline-block mt-2">
          Открыть задачу
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonComponent from "@/components/tools/Button.vue";
import {Message, Messages} from "@/data/messages";
import {Marketplaces} from "@/data/store";
import {ozonResetCategoryErrors} from "@/api/offer";
import {getInfo} from "@/api/task";

const POLL_INTERVAL = 4000

const TASK_TYPES = {
  ozon: [
    {value: 'ozon_reset_category_errors', label: 'Сброс ошибок категорий Ozon'},
  ],
}

const TASK_RUNNERS = {
  ozon_reset_category_errors: (storeId, dryRun) => ozonResetCategoryErrors(storeId, dryRun),
}

const RESULT_DESCRIPTIONS = {
  'in_progress': 'В процессе',
  'success': 'Успешно выполнена',
  'failure': 'Произошла ошибка',
}

export default {
  name: 'StoreOfferTasks',
  components: {ButtonComponent},
  props: {
    stores: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedStoreId: '',
      selectedTaskType: '',
      dryRun: true,
      running: false,
      task: null,
      pollTimer: null,
    }
  },
  computed: {
    supportedStores() {
      return this.stores.filter((store) => TASK_TYPES[store.marketplace])
    },
    selectedStore() {
      return this.stores.find((store) => store.id === this.selectedStoreId)
    },
    availableTaskTypes() {
      return TASK_TYPES[this.selectedStore?.marketplace] || []
    },
    canRun() {
      return !!this.selectedStoreId && !!this.selectedTaskType && !this.running
    },
    isInProgress() {
      return this.task?.task_result === 'in_progress'
    },
    isFailure() {
      return this.task?.task_result === 'failure'
    },
    progress() {
      return this.task?.task_progress ?? 0
    },
    resultDescription() {
      return RESULT_DESCRIPTIONS[this.task?.task_result] || this.task?.task_result || ''
    },
    resultBadgeClass() {
      const map = {
        'in_progress': 'bg-label-info',
        'success': 'bg-label-success',
        'failure': 'bg-label-danger',
      }
      return map[this.task?.task_result] || 'bg-label-secondary'
    },
  },
  methods: {
    marketplaceTitle(code) {
      return Marketplaces[code]?.name || code
    },
    onStoreChange() {
      this.selectedTaskType = ''
    },
    async run() {
      if (!this.canRun) {
        return
      }
      const runner = TASK_RUNNERS[this.selectedTaskType]
      if (!runner) {
        return
      }
      this.running = true
      this.$refs.runButton.startLoading()
      const message = new Message()
      try {
        const result = await runner(this.selectedStoreId, this.dryRun)
        this.task = result.data
        if (this.isInProgress) {
          this.startPolling()
        } else {
          this.running = false
        }
      } catch (exception) {
        this.running = false
        message.type = 'failure'
        message.text = exception.response?.data?.detail || 'Не удалось запустить задачу'
        Messages.list.push(message)
      } finally {
        this.$refs.runButton.endLoading()
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          const result = await getInfo(this.task.id)
          this.task = result.data
          if (!this.isInProgress) {
            this.stopPolling()
            this.running = false
            this.notifyResult()
          }
        } catch (e) {
          this.stopPolling()
          this.running = false
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    notifyResult() {
      const message = new Message()
      if (this.task?.task_result === 'success') {
        message.type = 'success'
        message.autoremove = 10
        message.text = this.task.task_info || 'Задача успешно выполнена'
      } else {
        message.type = 'failure'
        message.text = this.task?.task_info || 'Произошла ошибка при выполнении задачи'
      }
      Messages.list.push(message)
    },
  },
  beforeUnmount() {
    this.stopPolling()
  },
}
</script>
